'use client';

import { generateEmailHash } from '@hyphengroup/cms-components/utils/emailHash';
import CryptoJS from 'crypto-js';
import { track } from 'utils/tracking';
import { APPLY_V3_STEPS } from './stepTracking';
import {
  createBaseTracking,
  createItemTracking,
  TrackProduct,
} from './tracking.shared';

export type ApplyV3LoginMethod = 'email' | 'google' | 'prelogin';

export function createApplyNowUserData(email: string) {
  const normalizedEmail = email.trim().toLocaleLowerCase();
  const emailHashMd5 = generateEmailHash(normalizedEmail);

  return {
    email: normalizedEmail,
    email_hash_md5: emailHashMd5,
    email_hash_sha256: CryptoJS.SHA256(normalizedEmail).toString(),
  } as const;
}

export function trackApplyV3ProductCardDetails(product: TrackProduct): void {
  track({
    event: 'product_interaction',
    action: 'click',
    component_type: 'card',
    component_title: 'Product Card_See Full Card Details',
    item_list_name: 'apply form',
    is_final_step: 'false',
    is_lead_capture: 'false',
    ...createItemTracking(product),
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.capture.number,
      APPLY_V3_STEPS.capture.name,
    ),
  });
}

export function trackApplyV3RewardList(product: TrackProduct): void {
  track({
    event: 'product_interaction',
    action: 'click',
    component_type: 'card',
    component_title: 'Reward Card_See Full Reward List',
    item_list_name: 'apply form',
    ...createItemTracking(product),
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.capture.number,
      APPLY_V3_STEPS.capture.name,
    ),
  });
}

export function trackApplyV3ConsentInteraction(
  product: TrackProduct,
  checked: boolean,
): void {
  const { vertical_code, step_number, step_name } = createBaseTracking(
    product,
    APPLY_V3_STEPS.capture.number,
    APPLY_V3_STEPS.capture.name,
  );

  track({
    event: 'form_interact',
    lead_form_name: 'reward-lead-detail-form',
    form_type: 'reward form',
    action: checked ? 'check' : 'uncheck',
    component_type: 'checkbox',
    component_title: 'Lead Details/Checkbox_Marketing Consent',
    form_field_value: '<<PII redacted>>',
    vertical_code,
    step_number,
    step_name,
  });
}

export function trackApplyV3EmailFocus(product: TrackProduct): void {
  const { vertical_code, step_number, step_name } = createBaseTracking(
    product,
    APPLY_V3_STEPS.capture.number,
    APPLY_V3_STEPS.capture.name,
  );

  track({
    event: 'form_interact',
    lead_form_name: 'reward-lead-detail-form',
    form_type: 'reward form',
    action: 'fill',
    component_type: 'textbox',
    component_title: 'Lead Details/Textbox_Email',
    form_field_value: '<<PII redacted>>',
    vertical_code,
    step_number,
    step_name,
  });
}

export function trackApplyV3ApplyNow(
  product: TrackProduct,
  email: string,
  marketingConsent: boolean,
  loginMethod: ApplyV3LoginMethod,
): void {
  const userData = createApplyNowUserData(email);
  const { email_hash_md5, email_hash_sha256 } = userData;

  track({
    event: 'reward_gateway',
    intent: 'Confirm Lead Details',
    login_method: loginMethod,
    search_result_parameter: '',
    marketing_consent: marketingConsent ? 'yes' : 'no',
    user_data: {
      email_hash_md5,
      email_hash_sha256,
    },
    ...createItemTracking(product),
    component_type: 'bottom-sticky',
    component_title: 'Lead Details/CTA_Apply Now',
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.capture.number,
      APPLY_V3_STEPS.capture.name,
    ),
  });
}
